import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueries } from '@tanstack/react-query'
import DashIcon   from '../../components/ui/DashIcon'
import DashAvatar from '../../components/ui/DashAvatar'
import StatusBadge from '../../components/ui/StatusBadge'
import { deliveriesAPI } from '../../api/deliveries'
import type { Delivery, PaginatedResponse } from '../../types'

const STATUS_MAP: Record<string, string> = {
  active:    'in_transit',
  pending:   'accepted,assigned,picked_up',
  delivered: 'delivered',
  cancelled: 'cancelled,failed,returned',
}

const TABS = [
  { id: 'active',    label: 'En cours'    },
  { id: 'pending',   label: 'À récupérer' },
  { id: 'delivered', label: 'Livrés'      },
  { id: 'cancelled', label: 'Annulés'     },
]

const PAGE_SIZE = 12

const openGoogleMaps = (address: string) =>
  window.open(`https://www.google.com/maps/search/${encodeURIComponent(address)}`, '_blank')

export default function LivreurColis() {
  const [tab, setTab]                 = useState('active')
  const [page, setPage]               = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch]           = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1) }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const { data, isLoading } = useQuery<PaginatedResponse<Delivery>>({
    queryKey: ['my-deliveries', tab, page, search],
    queryFn: async () => {
      return deliveriesAPI.getAll({
        status: STATUS_MAP[tab],
        page,
        limit:  PAGE_SIZE,
        search: search || undefined,
      })
    },
  })

  const countResults = useQueries({
    queries: TABS.map(t => ({
      queryKey: ['my-deliveries-count', t.id],
      queryFn: () => deliveriesAPI.getAll({ status: STATUS_MAP[t.id], page: 1, limit: 1 }),
      staleTime: 30_000,
    })),
  })
  const tabCounts = Object.fromEntries(
    TABS.map((t, i) => [t.id, (countResults[i].data as PaginatedResponse<Delivery> | undefined)?.total ?? 0])
  )

  const deliveries = data?.data ?? []
  const total      = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const switchTab = (id: string) => {
    setTab(id)
    setPage(1)
    setSearchInput('')
    setSearch('')
  }

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Mes colis</h1>
          <p>Prises en charge, livraisons en cours et historique.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost"><DashIcon name="download" size={14} /> Exporter</button>
          <button className="btn btn-primary"><DashIcon name="plus" size={14} /> Scanner colis</button>
        </div>
      </div>

      <div className="tbl-card">
        <div className="tbl-toolbar">
          <div className="tbl-tabs">
            {TABS.map(t => (
              <button
                key={t.id}
                className={`tbl-tab${tab === t.id ? ' is-active' : ''}`}
                onClick={() => switchTab(t.id)}
              >
                {t.label}
                {tabCounts[t.id] > 0 && <span className="count">{tabCounts[t.id]}</span>}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <div className="tbl-search">
            <DashIcon name="search" size={14} />
            <input
              placeholder="ID colis, adresse, client…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
          <button className="btn btn-sm btn-ghost"><DashIcon name="filter" size={14} /> Filtres</button>
        </div>

        <div className="tbl-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th>Colis</th>
                <th>Client</th>
                <th>Trajet</th>
                <th>Statut</th>
                <th>COD</th>
                <th>Gain</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted)', padding: 32 }}>
                    Chargement…
                  </td>
                </tr>
              ) : deliveries.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>
                    Aucun colis dans cet onglet.
                  </td>
                </tr>
              ) : deliveries.map((o) => (
                <tr key={o._id}>
                  <td><span className="row-id">{o.trackingCode}</span></td>
                  <td>
                    <div className="cell-user">
                      <DashAvatar name={o.dropoff.contactName ?? '?'} size={28} />
                      <div>
                        <b>{o.dropoff.contactName ?? '—'}</b>
                        <span>{o.dropoff.contactPhone ?? '—'}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="cell-route">
                      <span className="from">{(o.pickup?.address ?? 'Expéditeur').split(',')[0]}</span>
                      <span className="arrow">↓</span>
                      <span className="to">{o.dropoff.address.split(',')[0]}</span>
                    </div>
                  </td>
                  <td><StatusBadge status={o.status} /></td>
                  <td className="mono">{o.price?.base ?? 0} DT</td>
                  <td>
                    <span style={{ color: 'var(--green-700)', fontWeight: 600 }} className="mono">
                      +{o.price?.livreurAmount ?? 0} DT
                    </span>
                  </td>
                  <td className="col-actions">
                    <button onClick={() => openGoogleMaps(o.dropoff.address)}>Naviguer</button>
                    <button onClick={() => navigate(`/livreur/colis/${o._id}`)}>Détails</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{
          padding: '14px 18px', borderTop: '1px solid var(--line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: '.82rem', color: 'var(--muted)',
        }}>
          <span>
            {total > 0
              ? `Affichage ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} sur ${total} colis`
              : 'Aucun colis'}
          </span>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >←</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setPage(p)}
                >{p}</button>
              ))}
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >→</button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
